import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { debounce } from 'lodash'
import {
    memoize,
    renderTimeXAxis,
    renderYAxis,
    renderHorizontalGridlines,
    renderLine,
    renderPips,
    renderLegend,
    renderHoverState,
} from 'widgets/components/visualization/d3Functions'

import { useRefDimensions } from 'widgets/components/visualization/hooks/visualizationHooks'

const DATA_POINTS_PER_PIXEL = 0.0635
// This function preprocess the data for line, passing it through
// d3's time parser, as well as doing the accumulation if needed.
const getDataForLineGraph = memoize(['timestamp', 'width', 'colors'], (params) => {
    const { rawData, cumulative, width, colors } = params

    let parsedData
    const parseFunc = datum => [d3.time.format('%Y-%m-%d').parse(datum[0]), datum[1]]

    // how many dots (data poitns) can we fit in this widget at this width
    const maxDataPoints = width * DATA_POINTS_PER_PIXEL

    // if theres only one data point, lets go ahead and make it an array
    // then, use parseFunc to transform the string into d3 time obj
    if (rawData.length) {
        parsedData = rawData.map(datum => Object.entries(datum).map(parseFunc).sort((a, b) => a[0] - b[0]))
    } else {
        parsedData = [Object.entries(rawData).map(parseFunc).sort((a, b) => a[0] - b[0])]
    }

    // copy the colors obj
    const parsedColors = []
    // lets go ahead and filter out any datasourcee that returned no data
    parsedData = parsedData.filter(
        (datum, i) => {
            if (datum.length) {
                parsedColors.push(colors[i])
                return true
            }
            return false
        }
    )


    // now, for every data source that has more data points then we can fit, lets slice
    // that to just the max amount of latest (righmost on the array) data points
    let hasSliced = false
    parsedData = parsedData.map(datum => {
        if (datum.length > maxDataPoints) {
            hasSliced = true
            return datum.slice(-maxDataPoints)
        }
        return datum
    })

    // if there are more than one data line, and we have had to slice at least one line's data
    // we then need to cap all other lines to not show any results earlier than the left most
    // data point we can fit for the line that has the most data.
    if (hasSliced && parsedData.length > 1) {
        let maxFirstItem = parsedData[0][0][0]
        parsedData.forEach((datum) => {
            if (maxFirstItem < datum[0][0]) {
                maxFirstItem = datum[0][0]
            }
        })

        parsedData = parsedData.map(datum => datum.filter(each => each[0] >= maxFirstItem))
    }

    if (cumulative) {
        parsedData = parsedData.map((datum) => {
            let accumulation = 0
            return datum.map((point) => {
                accumulation += point[1]
                return [point[0], accumulation]
            })
        })
    }
    return [parsedData, parsedColors]
})

const renderLineGraph = ({
    colors,
    cumulative,
    data,
    height,
    editing,
    interval,
    labels,
    line,
    timestamp,
    widgetKey,
    width,
}) => {
    const svg = d3.select(line).attr('viewBox', [0, 0, width, height])
    const margins = { y: 50, x: 30 }
    height = height - (margins.y * 2)
    width = width - (margins.x * 2) - 20
    let g

    // on rerenders, we shouldnt append an additional `g` tag. However, `.select` is always truthy
    // regardless of whether it finds an element matching the function call args. Howver, by indexing
    // into it, we are able to check whether it actually found a g element.
    if (svg.select('g')[0][0]) {
        g = svg.select('g')
    } else {
        g = svg.append('g')
    }
    g.attr('transform', `translate(${margins.y}, ${margins.x})`)
    const [parsedData, parsedColors] = getDataForLineGraph({ timestamp, rawData: data, cumulative, width, colors, widgetKey })

    const params = {
        // main building block `g` element (most things get added as children to it)
        g,
        // overall svg element. Most things dont need to directly use it, appending to `g` instead
        svg,
        // width of the container for the graph
        width,
        // height of the container
        height,
        // array of labels for each datum
        labels,
        // whether or not we are on edit mode, which alters how we deal with hover states (and todo: line clicks)
        editing,
        // timestamp of data fetch, used to memoize based on data
        timestamp,
        // unique id for this widget
        widgetKey,
        // array of datums, each representing the points in a line. post preprossecing done in getDataForLineGraph
        data: parsedData,
        // array of colors corresponding to each datum
        colors: parsedColors,
        // original colors to be used for legends,
        originalColors: colors,
        timeFormat: (interval && DjangIO.app.widgets.types.LineGraphTimeIntervals.by_value(interval).time_format) || '%e %b \'%y',
    }

    renderTimeXAxis(params)
    renderYAxis(params)
    renderHorizontalGridlines(params)
    renderLine(params)
    renderPips(params)
    renderLegend(params)
    renderHoverState(params)
}

const debouncedRenderLineGraph = debounce(renderLineGraph, 500, { trailing: true, leading: true })


const Line = ({
    colors,
    cumulative,
    data,
    editing,
    editForm,
    interval,
    labels,
    timestamp,
    widgetKey,
}) => {
    const line = useRef()
    const { height, width } = useRefDimensions({
        timestamp,
        reference: line,
    })

    // if we are on the editform, we wanna debounce the render as changing makeProps
    // will be flying in. However, on dash itself, common debouncing is not applied
    // on a per widget basis, so we dont want to cause the debouncing of one widget
    // to throttle the renderng of another.
    const renderFunc = editForm ? debouncedRenderLineGraph : renderLineGraph

    if (data && height && width) {
        renderFunc({
            colors,
            cumulative,
            data,
            editing,
            height,
            interval,
            labels,
            timestamp,
            widgetKey,
            width,
            line: line.current,
        })
    }

    return (
        <svg
            ref={line}
            style={{ flexGrow: 1 }}
        />
    )
}

Line.defaultProps = {}

Line.propTypes = {
    colors: PropTypes.object,
    cumulative: PropTypes.bool,
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    interval: PropTypes.number,
    labels: PropTypes.arrayOf(PropTypes.string),
    timestamp: PropTypes.number,
    widgetKey: PropTypes.string,
}

export default Line
